import React, { Dispatch, SetStateAction, useState } from 'react';
import { FormGroup, TreeViewDataItem } from '@patternfly/react-core';
import { useMount } from 'react-use';
import { TDataframe } from '../../../../api/types';
import { TDataframeData } from '../../../../api/types/dataframe-data';
import { Dataframe } from '../../../../api/dataframes/Dataframes';
import SearchableTreeViewSelect from '../../../form/Select/SearchableTreeViewSelect';

export type CalculatedFactViewProps = {
	onSelectDataframe: Dispatch<SetStateAction<TDataframe | undefined>>;
	selectedDataframe?: TDataframe;
	onSelectCalculatedDataset: Dispatch<SetStateAction<TDataframeData | undefined>>;
	selectedCalculatedDataset?: TDataframeData;
	setCreateDisabled: Dispatch<SetStateAction<boolean>>;
};

const buildDataframeTreeItem = (df: TDataframe): TreeViewDataItem => ({
	id: String(df.id),
	name: df.name,
});

const buildDatasetTreeItem = (d: TDataframeData): TreeViewDataItem => ({
	id: String(d.id),
	name: d.title ?? String(d.id),
});

function CalculatedFactView(props: CalculatedFactViewProps) {
	const [dataframes, setDataframes] = useState<TDataframe[]>([]);
	const [fetchError, setFetchError] = useState<boolean>(false);

	useMount(() => {
		props.onSelectCalculatedDataset(undefined);
		props.setCreateDisabled(true);
		void Dataframe.GetAll(['datasets'])
			.then((all) => {
				setDataframes(all.filter((df) => df.datasets.some((d) => d.calculated)));
			})
			.catch(() => {
				setFetchError(true);
			});
	});

	const onSelectDataframe = (_event: React.MouseEvent, item: TreeViewDataItem) => {
		const df = dataframes.find((d) => String(d.id) === item.id);
		if (!df) return;
		props.onSelectDataframe(df);
		props.onSelectCalculatedDataset(undefined);
		props.setCreateDisabled(true);
	};

	const onSelectDataset = (_event: React.MouseEvent, item: TreeViewDataItem) => {
		const dataset = props.selectedDataframe?.datasets.find(
			(d) => d.calculated && String(d.id) === item.id
		);
		if (!dataset) return;
		props.onSelectCalculatedDataset(dataset);
		props.setCreateDisabled(false);
	};

	const emptyTree: TreeViewDataItem[] = [{ id: '-1', name: 'No results found' }];

	const calculatedDatasets = props.selectedDataframe?.datasets.filter((d) => d.calculated) ?? [];

	return (
		<div className="calculated-fact-view">
			{fetchError && (
				<p className="calculated-fact-view__error">
					Failed to load dataframes. Please try again.
				</p>
			)}
			<FormGroup label="Dataframe">
				<div className="form-group-col">
					<SearchableTreeViewSelect
						data={
							dataframes.length > 0
								? dataframes.map(buildDataframeTreeItem)
								: emptyTree
						}
						placeHolderText={props.selectedDataframe?.name ?? 'Select a Dataframe'}
						onSelect={onSelectDataframe}
						treeItemsExpanded={true}
					/>
				</div>
			</FormGroup>
			{props.selectedDataframe && (
				<FormGroup label="Calculated Fact">
					<div className="form-group-col">
						<SearchableTreeViewSelect
							data={
								calculatedDatasets.length > 0
									? calculatedDatasets.map(buildDatasetTreeItem)
									: emptyTree
							}
							placeHolderText={
								props.selectedCalculatedDataset?.title ?? 'Select a Calculated Fact'
							}
							onSelect={onSelectDataset}
							treeItemsExpanded={true}
						/>
					</div>
				</FormGroup>
			)}
		</div>
	);
}

export default CalculatedFactView;
